/**
 * Thin hook shims over TanStack Router, giving `components/*` and `lib/*`
 * files a `useRouter`/`usePathname`/`useSegments`/`useLocalSearchParams`
 * surface. (Route files under `app/` use the native TanStack hooks directly.)
 */
import {
  useNavigate,
  useRouterState,
  useParams,
  useSearch,
} from "@tanstack/react-router";

import { routeOptions } from "./use-app-navigate";

/**
 * Call sites pass either a string path or an object form
 * (`{ pathname, params }`). TanStack takes `{ to, search }`, so normalize both
 * into that shape in one place.
 */
type To = string | { pathname: string; params?: Record<string, unknown> };

/**
 * Strip pathless-group segments (`/(app)`, `/(auth)`) from a `to`
 * path. TanStack route groups don't appear in the URL, so `/(app)/admin` must
 * become `/admin`. Many component-level nav calls pass group-prefixed paths,
 * so normalize them here in one place.
 */
function stripGroups(pathname: string): string {
  const cleaned = pathname.replace(/\/\([^)]+\)/g, "");
  return cleaned === "" ? "/" : cleaned;
}

/**
 * Turn a `To` (string or `{ pathname, params }`) into TanStack's
 * `{ to, search }`. Non-dynamic `params` become query/search params, which is
 * how the routes consume them (`validateSearch` + `Route.useSearch()`).
 */
function toNavOptions(to: To, extra?: { replace?: boolean }) {
  if (typeof to === "string") return routeOptions(stripGroups(to), undefined, extra);
  return routeOptions(stripGroups(to.pathname), to.params, extra);
}

export function useRouter() {
  const navigate = useNavigate();
  return {
    push: (to: To) => navigate(toNavOptions(to)),
    replace: (to: To) => navigate(toNavOptions(to, { replace: true })),
    navigate: (to: To) => navigate(toNavOptions(to)),
    back: () => window.history.back(),
    canGoBack: () => window.history.length > 1,
    setParams: (params: Record<string, unknown>) =>
      navigate({
        to: ".",
        search: (prev: Record<string, unknown>) => ({ ...prev, ...params }),
        replace: true,
      }),
  };
}

export function usePathname(): string {
  return useRouterState({ select: (s) => s.location.pathname });
}

/**
 * A yes/no fact about the pathname, subscribed at the fact's own granularity:
 * the component re-renders only when the ANSWER flips, not on every
 * navigation. `usePathname` re-renders on each route change, which is the
 * right contract for a component that shows the route and the wrong one for a
 * component that only checks it — a long-lived dialog asking "am I on the
 * map?" would otherwise pay a render for every click in the sidebar.
 */
export function usePathnameFlag(test: (pathname: string) => boolean): boolean {
  return useRouterState({ select: (s) => test(s.location.pathname) });
}

export function useSegments(): string[] {
  const pathname = usePathname();
  return pathname.split("/").filter(Boolean);
}

export function useLocalSearchParams<
  T extends Record<string, unknown> = Record<string, string>,
>(): T {
  const params = useParams({ strict: false });
  const search = useSearch({ strict: false });
  return { ...(params as object), ...(search as object) } as T;
}
